<script lang="ts">
    import Vue from "vue";
    import { Component } from 'vue-facing-decorator';
    import { EditorState } from '@common/Common';
    import { CreateNotificationEventPayload, NotificationEditorViewModel, UpdateNotificationEventPayload } from '@Alternatives/Notifications/Components/NotificationEditor';


    @Component({
        props: {
            viewModel: {
                type: Object,
                required: true
            }
        }
    })
    export default class NotificationEditor extends Vue
    {
        get vm(): NotificationEditorViewModel { return this.$props.viewModel as NotificationEditorViewModel; }
        get opened() : boolean { return this.vm?.opened ?? false; }
        get modalTitle(): string { return this.vm.state === EditorState.Creator ? 'Create notification' : 'Edit notification'; }
        get confirmButtonLabel(): string { return this.vm.state === EditorState.Creator ? 'Create' : 'Update'; }

        get statuses() { return this.vm.statuses; }
        get attributes() { return this.vm.attributes; }
        get emailSnippets() { return this.vm.emailSnippets; }
        get error() { return this.vm.error; }

        get enabled(): boolean { return this.vm.enabled; }
        set enabled(val: boolean) { this.vm.enabled = val; }

        get name(): string { return this.vm.name; }
        set name(val: string) { this.vm.name = val; }

        get description(): string | null { return this.vm.description; }
        set description(val: string | null) { this.vm.description = val; }

        get statusId(): number | null { return this.vm.statusId; }
        set statusId(val: number | null) { this.vm.statusId = val; }

        get prevStatusId(): number | null { return this.vm.prevStatusId; }
        set prevStatusId(val: number | null) { this.vm.prevStatusId = val; }

        get emailSubject(): string | null { return this.vm.emailSubject; }
        set emailSubject(val: string | null) { this.vm.emailSubject = val; }

        get emailReplyToEmail(): string | null { return this.vm.emailReplyToEmail; }
        set emailReplyToEmail(val: string | null) { this.vm.emailReplyToEmail = val; }

        get emailRecipientAttributeId(): number | null { return this.vm.emailRecipientAttributeId; }
        set emailRecipientAttributeId(val: number | null) { this.vm.emailRecipientAttributeId = val; }

        get emailAdHocRecipients(): string | null { return this.vm.emailAdHocRecipients; }
        set emailAdHocRecipients(val: string | null) { this.vm.emailAdHocRecipients = val; }

        get emailMessage(): string | null { return this.vm.emailMessage; }
        set emailMessage(val: string | null) { this.vm.emailMessage = val; }

        insertEmailSnippet(content: string)
        {
            this.emailMessage = (this.emailMessage ?? "") + content;
        }

        onFormSubmit()
        {
            this.vm.clearValidationErrors();

            if (this.vm.state === EditorState.Creator)
            {
                const payload = new CreateNotificationEventPayload(this.name, this.description, this.enabled, this.statusId, this.prevStatusId, this.emailSubject, this.emailMessage, this.emailRecipientAttributeId, this.emailAdHocRecipients, this.emailReplyToEmail);
                this.$emit("create-notification", payload);
            }
            else if (this.vm.state === EditorState.Editor)
            {
                const payload = new UpdateNotificationEventPayload(this.vm.id, this.name, this.description, this.enabled, this.statusId, this.prevStatusId, this.emailSubject, this.emailMessage, this.emailRecipientAttributeId, this.emailAdHocRecipients, this.emailReplyToEmail);
                this.$emit("update-notification", payload);
            }
        }

        close()
        {
            this.vm.close();
        }
    }
</script>

<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ modalTitle }}</h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>
                    
                    <div class="modal-body">
                        <div v-if="error" class="alert alert-danger" role="alert">
                            <strong>{{error.ErrorCode.substring(0, error.ErrorCode.indexOf("."))}}.</strong> {{error.Error}}
                        </div>

                        <form id="notification-editor-form" @submit.prevent="onFormSubmit">
                            <div class="row">
                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>
                                            <input type="checkbox" v-model="enabled"> Active
                                        </label>
                                    </div>

                                    <div class="form-group">
                                        <label>Name (2 - 100 characters):</label>
                                        <input type="text" class="form-control" v-model.trim="name" required minlength="2" maxlength="100">
                                    </div>

                                    <div class="form-group">
                                        <label>Description (optional):</label>
                                        <textarea class="form-control" rows="6" v-model.trim="description"></textarea>
                                    </div>

                                    <div class="form-group">
                                        <label>Send when status is changed to:</label>
                                        <select v-model="statusId" class="form-control">
                                            <option v-for="s in statuses" :value="s.Id">{{s.Name}}</option>
                                        </select>
                                    </div>

                                    <div class="form-group">
                                        <label>Send when previous status was:</label>
                                        <select v-model="prevStatusId" class="form-control">
                                            <option :value="null">Any</option>
                                            <option v-for="s in statuses" :value="s.Id">{{s.Name}}</option>
                                        </select>
                                    </div>
                                </div>

                                <div class="col-sm-6">
                                    <div class="form-group">
                                        <label>Email subject:</label>
                                        <input type="text" class="form-control" v-model.trim="emailSubject" required minlength="2" maxlength="500">
                                    </div>
                                    <div class="form-group">
                                        <label>Reply to email:</label>
                                        <input type="email" class="form-control" v-model.trim="emailReplyToEmail" required>
                                    </div>

                                    <div class="form-group">
                                        <label>Recipients:</label><br />
                                        <small class="text-muted">Email recipients could be provided in two ways: from attribute and/or list of ad hoc emails separated by comma.</small>
                                        <select v-model="emailRecipientAttributeId" class="form-control">
                                            <option :value="null">Select attribute...</option>
                                            <option v-for="attr in attributes" :value="attr.Id">{{attr.Name}}</option>
                                        </select>
                                        <input type="text" class="form-control" placeholder="Add hoc emails separated by comma" v-model.trim="emailAdHocRecipients" />
                                    </div>

                                    <div class="form-group">
                                        <label>Message:</label>
                                        <small class="text-muted">Message that will be send to the recipients.</small>
                                        <div class="pull-right">
                                            <div class="dropdown">
                                                <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-toggle="dropdown">Insert</button>
                                                <div class="dropdown-menu" style="height: 50vh; min-width: 300px; min-height: 300px; overflow-y: auto;">
                                                    <a v-for="es in emailSnippets" class="dropdown-item" @click="insertEmailSnippet(es.Content)">{{es.Title}}</a>
                                                </div>
                                            </div>
                                        </div>
                                        <textarea class="form-control" rows="6" v-model.trim="emailMessage" required></textarea>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="submit" form="notification-editor-form" class="btn btn-primary">{{ confirmButtonLabel }}</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template> 